import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Input,
  List,
  ListItem,
  Text,
  Spinner,
  FormLabel,
} from '@chakra-ui/react';
import { getData } from '../../utils/httpClient';
import useBcuUsdRate from '../../hooks/useBcuUsdRate';
import { formatMoneyDual } from '../../utils/currencyDisplay';

/**
 * Buscador de productos por código o nombre (catálogo, no stock).
 */
const ProductSearchSelect = ({
  value,
  onChange,
  label = 'Producto',
  placeholder = 'Buscar por N° de pieza o nombre…',
  isDisabled = false,
  size = 'sm',
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const { rate: usdRate } = useBcuUsdRate();

  const search = useCallback(async (q) => {
    if (!q || q.trim().length < 1) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await getData(
        `/products?page=1&limit=30&data=${encodeURIComponent(q.trim())}`,
        { skipLoader: true },
      );
      setResults(res?.payload?.products || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), 280);
    return () => clearTimeout(t);
  }, [query, search]);

  useEffect(() => {
    if (!value) {
      setSelectedLabel('');
      return;
    }
    const found = results.find((p) => String(p.id) === String(value));
    if (found) {
      setSelectedLabel(`${found.code || ''} — ${found.name}`.trim());
    }
  }, [value, results]);

  const handlePick = (product) => {
    onChange(product.id, product);
    setSelectedLabel(`${product.code || ''} — ${product.name}`.trim());
    setQuery('');
    setOpen(false);
    setResults([]);
  };

  return (
    <Box position="relative" mb={3}>
      {label && (
        <FormLabel fontSize="sm" mb={1}>
          {label}
        </FormLabel>
      )}
      {value && selectedLabel && !open ? (
        <Box
          p={2}
          borderWidth="1px"
          borderRadius="md"
          bg="gray.50"
          fontSize="sm"
          cursor={isDisabled ? 'default' : 'pointer'}
          onClick={() => !isDisabled && setOpen(true)}
        >
          {selectedLabel}
          {!isDisabled && (
            <Text as="span" color="gray.500" ml={2} fontSize="xs">
              (cambiar)
            </Text>
          )}
        </Box>
      ) : (
        <Input
          size={size}
          placeholder={placeholder}
          value={query}
          isDisabled={isDisabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
        />
      )}
      {open && (loading || results.length > 0 || query.length > 0) && (
        <List
          position="absolute"
          zIndex={20}
          w="100%"
          mt={1}
          bg="white"
          borderWidth="1px"
          borderRadius="md"
          maxH="200px"
          overflowY="auto"
          boxShadow="md"
        >
          {loading && (
            <ListItem px={3} py={2}>
              <Spinner size="sm" />
            </ListItem>
          )}
          {!loading && results.length === 0 && query.length > 0 && (
            <ListItem px={3} py={2} fontSize="sm" color="gray.500">
              Sin resultados
            </ListItem>
          )}
          {!loading &&
            results.map((p) => (
              <ListItem
                key={p.id}
                px={3}
                py={2}
                fontSize="sm"
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
                onMouseDown={() => handlePick(p)}
              >
                <Text fontWeight="medium">{p.name}</Text>
                <Text fontSize="xs" color="gray.500">
                  {p.code ? `N° pieza: ${p.code}` : 'Sin N° de pieza'}
                  {p.currentCost != null && (
                    <>
                      {' · '}
                      {formatMoneyDual(
                        p.currentCost,
                        p.currency?.code || 'UYU',
                        usdRate || p.currency?.rate,
                      )}
                    </>
                  )}
                </Text>
              </ListItem>
            ))}
        </List>
      )}
    </Box>
  );
};

export default ProductSearchSelect;
